import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { MeResponse } from "@/lib/types";

interface AuthState {
  status: "loading" | "authed" | "anonymous";
  me: MeResponse | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const activeOrgRef = useRef<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const refresh = async () => {
    try {
      const fresh = await api.me();
      if (activeOrgRef.current && activeOrgRef.current !== fresh.org.id) queryClient.clear();
      activeOrgRef.current = fresh.org.id;
      setMe(fresh);
      setStatus("authed");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
        setStatus("anonymous");
      } else {
        // Network or 500 — surface as anonymous; ErrorBoundary catches deeper errors.
        setMe(null);
        setStatus("anonymous");
      }
    }
  };

  const logout = async () => {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
    setMe(null);
    setStatus("anonymous");
    window.location.href = "/login";
  };

  useEffect(() => {
    void refresh();
  }, []);

  return <Ctx.Provider value={{ status, me, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error("useAuth() must be inside <AuthProvider>");
  }
  return ctx;
}
